import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { apiClient } from "../../api/client";
import { useAppSelector } from "../../store/hooks";
import { Button } from "../../ui/components/Button";
import { haptic } from "../../utils/haptics";

type SyncResponse = {
  status?: string;
  action?: "fetch_image" | "show_registration" | "show_sale";
  registration?: {
    claimUrl?: string;
    nextRefreshAt?: string; // ISO UTC
    refreshIntervalMin?: number;
  };
  sale?: {
    price?: number;
    currency?: string;
    contactEmail?: string;
    buyUrl?: string;
    description?: string;
  };
};

const Wrap = styled.div`
  min-height: calc(100vh - 64px);
  background: #f0f0f0;
  padding: 1.25rem;
`;

const ContentMax = styled.div`
  max-width: 840px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  margin: 0 0 0.35rem;
  font-size: 1.65rem;
`;

const PageSubtitle = styled.p`
  margin: 0 0 1.25rem;
  color: #5f5f5f;
  line-height: 1.45;
`;

const CardBase = styled.div`
  background: #ffffff;
  border: 2px solid #111;
  box-shadow: 6px 6px 0 #111;
  padding: 1.35rem 1.5rem;
`;

const Muted = styled.p`
  color: #5f5f5f;
  margin: 0;
  line-height: 1.45;
`;

const MutedSmall = styled.p`
  color: #7a7a7a;
  font-size: 0.88rem;
  margin: 0.75rem 0 0;
  text-align: center;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border: 1px solid #232323;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const Row = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
`;

const DeviceInput = styled.input`
  flex: 1;
  min-width: 200px;
  border: 2px solid #111;
  border-radius: 4px;
  padding: 0.55rem 0.7rem;
  font-size: 0.95rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`;

const ErrorText = styled.p`
  color: #a30000;
  font-weight: 600;
  margin: 0.75rem 0 0;
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 600;
`;

const LoadingLine = styled.p`
  margin: 0.85rem 0 0;
  font-weight: 600;
  color: #232323;
`;

const CardHeading = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 1.35rem;
`;

const CardSub = styled.p`
  margin: 0 0 1rem;
  color: #5f5f5f;
  font-size: 0.95rem;
  line-height: 1.45;
`;

const Section = styled.div`
  margin-top: 1rem;
  border-top: 1px solid #d7d7d7;
  padding-top: 1rem;
`;

const SuccessTitle = styled.h2`
  margin: 0 0 0.65rem;
  font-size: 1.5rem;
  line-height: 1.25;
`;

const CtaStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const primaryYellowStyles = css`
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 700;
  color: #111;
  background: #ffd24a;
  border: 2px solid #111;
  box-shadow: 6px 6px 0 #111;
  border-radius: 4px;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  text-align: center;

  &:hover:not(:disabled) {
    transform: translate(1px, 1px);
    box-shadow: 5px 5px 0 #111;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryYellowButton = styled.button`
  ${primaryYellowStyles}
`;

const PrimaryYellowLink = styled(Link)`
  ${primaryYellowStyles}
  text-decoration: none;
  display: block;
`;

const SecondaryWhiteLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.65rem 1rem;
  font-size: 0.98rem;
  font-weight: 700;
  color: #111;
  text-decoration: none;
  background: #fff;
  border: 2px solid #111;
  box-shadow: 6px 6px 0 #111;
  border-radius: 4px;
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translate(1px, 1px);
    box-shadow: 5px 5px 0 #111;
  }
`;

const FooterLink = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.92rem;

  a {
    color: #232323;
    font-weight: 600;
  }
`;

const RefreshCard = styled.div`
  margin-top: 1rem;
  padding: 0.75rem 0.9rem;
  border: 1px dashed #888;
  border-radius: 4px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const RefreshPrimary = styled.div`
  font-size: 0.95rem;
  color: #232323;
`;

const RefreshCountdown = styled.div`
  font-size: 0.85rem;
  color: #5f5f5f;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`;

function formatLocalHHMM(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function formatUtcHHMM(d: Date): string {
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return "refreshing…";
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

interface ScreenRefreshInfoProps {
  nextRefreshAt?: string;
  onExpire?: () => void;
}

const ScreenRefreshInfo: React.FC<ScreenRefreshInfoProps> = ({ nextRefreshAt, onExpire }) => {
  const [now, setNow] = useState<number>(() => Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [nextRefreshAt]);

  if (!nextRefreshAt) return null;
  const target = new Date(nextRefreshAt);
  if (isNaN(target.getTime())) return null;

  const remaining = target.getTime() - now;
  if (remaining <= 0 && !firedRef.current) {
    firedRef.current = true;
    // defer so we don't setState during render
    window.setTimeout(() => onExpire?.(), 0);
  }

  return (
    <RefreshCard>
      <RefreshPrimary>
        Frame screen refreshes at <Mono>{formatLocalHHMM(target)}</Mono> (UTC{" "}
        <Mono>{formatUtcHHMM(target)}</Mono>).
      </RefreshPrimary>
      <RefreshCountdown>next refresh in {formatCountdown(remaining)}</RefreshCountdown>
    </RefreshCard>
  );
};

const normalizeDeviceId = (value: string): string => value.trim().toUpperCase();

const AotdIrlClaim: React.FC = () => {
  const friendlyError = (rawMessage: string): string => {
    const t = (rawMessage || "").trim();
    if (!t) {
      return "Something went wrong. Try again or contact support if this persists.";
    }
    const map: Record<string, string> = {
      "invalid or expired pairing token":
        "This claim link is missing or expired. Re-scan the QR shown on your frame.",
      "device is currently in sale mode":
        "This frame is currently listed for sale. Contact support to claim it.",
      "device is already claimed by another user":
        "This frame is already claimed by someone else. If it's yours, contact support.",
      "deviceId is required": "Please enter the device ID shown on your frame.",
      "missing backend user id in token":
        "Something went wrong with your sign-in. Please sign out and sign in again.",
    };
    if (map[t]) return map[t];
    return `${t} Try again or contact support if this persists.`;
  };

  const navigate = useNavigate();
  const { deviceId: routeDeviceId } = useParams();
  const [searchParams] = useSearchParams();
  const pairingToken = searchParams.get("p");
  const stayOnPage = searchParams.get("stay") === "1";
  const auth = useAppSelector((state) => state.auth);

  const authReady = auth.bootstrapStatus === "ready";
  const isLoggedIn = authReady && Boolean(auth.token);

  const deviceIdFromUrl = useMemo(() => {
    return Boolean(routeDeviceId?.trim()) || Boolean(searchParams.get("deviceId")?.trim());
  }, [routeDeviceId, searchParams]);

  const initialDeviceId = useMemo(
    () => normalizeDeviceId(routeDeviceId || searchParams.get("deviceId") || ""),
    [routeDeviceId, searchParams]
  );

  const [deviceId, setDeviceId] = useState(initialDeviceId);
  const [syncData, setSyncData] = useState<SyncResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [claimedDeviceId, setClaimedDeviceId] = useState<string | null>(null);
  const [syncNonce, setSyncNonce] = useState(0);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);

  const claimSuccessRef = useRef(false);
  useEffect(() => {
    claimSuccessRef.current = claimSuccess;
  }, [claimSuccess]);

  const canFetch = deviceId.length > 0;

  useEffect(() => {
    setDeviceId(initialDeviceId);
  }, [initialDeviceId]);

  useEffect(() => {
    if (!canFetch) {
      setSyncData(null);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    apiClient
      .get("/api/aotd-irl/sync", { params: { deviceId } })
      .then((res) => {
        if (!active) return;
        setSyncData(res.data as SyncResponse);
      })
      .catch((err) => {
        if (!active) return;
        if (claimSuccessRef.current) return;
        const raw = err?.response?.data?.error;
        setError(friendlyError(typeof raw === "string" ? raw : "failed to load device state"));
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canFetch, deviceId, syncNonce]);

  const handleLookup = useCallback(() => {
    const normalized = normalizeDeviceId(deviceId);
    setDeviceId(normalized);
    if (!normalized) {
      setError(friendlyError("deviceId is required"));
      return;
    }
    setSyncNonce((n) => n + 1);
  }, [deviceId]);

  const handleClaim = async () => {
    if (!deviceId) return;

    setClaiming(true);
    setError(null);

    try {
      await apiClient.post("/api/aotd-irl/claim", {
        deviceId,
        ...(pairingToken ? { pairingToken } : {}),
      });
      setClaimSuccess(true);
      haptic("success");
      setClaimedDeviceId(normalizeDeviceId(deviceId));
      try {
        const refreshed = await apiClient.get("/api/aotd-irl/sync", { params: { deviceId } });
        setSyncData(refreshed.data as SyncResponse);
      } catch {
        // ignore sync errors after successful claim; success card is primary
      }
    } catch (err: unknown) {
      const apiErr =
        typeof (err as { response?: { data?: { error?: string } } })?.response?.data?.error ===
        "string"
          ? (err as { response: { data: { error: string } } }).response.data.error
          : "";
      setError(friendlyError(apiErr || "failed to claim display"));
    } finally {
      setClaiming(false);
    }
  };

  useEffect(() => {
    if (!claimSuccess || !isLoggedIn || stayOnPage) {
      setRedirectCountdown(null);
      return;
    }

    setRedirectCountdown(8);
    const navTimer = window.setTimeout(() => {
      navigate("/aotd-irl/my");
    }, 8000);

    const interval = window.setInterval(() => {
      setRedirectCountdown((c) => {
        if (c === null || c <= 1) return 0;
        return c - 1;
      });
    }, 1000);

    return () => {
      window.clearTimeout(navTimer);
      window.clearInterval(interval);
    };
  }, [claimSuccess, isLoggedIn, stayOnPage, navigate]);

  const hasDeviceId = deviceId.trim().length > 0;
  const hasPairingToken = Boolean(pairingToken?.trim());
  const displayIdForCopy = (claimedDeviceId || deviceId).trim();

  const loginReturnParam = encodeURIComponent(window.location.href);
  const signupReturnParam = loginReturnParam;

  const successCard =
    claimSuccess &&
    displayIdForCopy && (
      <CardBase>
        <SuccessTitle>Welcome — your frame is yours</SuccessTitle>
        <Muted style={{ marginBottom: "0.65rem" }}>
          Display ID: <Mono>{displayIdForCopy}</Mono>
        </Muted>
        <Muted>
          Your frame will pull the latest art the next time it wakes—usually within about an hour, or
          sooner if you press the wake button on the back.
        </Muted>
        <CtaStack>
          <PrimaryYellowButton type="button" onClick={() => navigate("/aotd-irl/my")}>
            Manage your frames
          </PrimaryYellowButton>
          <SecondaryWhiteLink to="/aotd-irl/setup">Set up another frame</SecondaryWhiteLink>
        </CtaStack>
        {isLoggedIn && !stayOnPage && redirectCountdown !== null && redirectCountdown > 0 && (
          <MutedSmall>redirecting in {redirectCountdown}…</MutedSmall>
        )}
        <MutedSmall>Bookmark /aotd-irl/my to come back any time.</MutedSmall>
      </CardBase>
    );

  const signInPromptCard =
    !claimSuccess &&
    authReady &&
    !isLoggedIn &&
    hasDeviceId &&
    hasPairingToken && (
      <CardBase>
        <CardHeading>Sign in to claim this frame</CardHeading>
        {loading && <LoadingLine>Looking up your frame…</LoadingLine>}
        {error && <ErrorText>{error}</ErrorText>}
        <Muted style={{ marginBottom: "0.75rem", marginTop: error || loading ? "0.65rem" : 0 }}>
          Signing in binds this display to your account. After that you can manage it from{" "}
          <Mono>/aotd-irl/my</Mono>.
        </Muted>
        <Muted style={{ marginBottom: "1rem" }}>
          Display ID: <Mono>{normalizeDeviceId(deviceId)}</Mono>
        </Muted>
        <ScreenRefreshInfo
          nextRefreshAt={syncData?.registration?.nextRefreshAt}
          onExpire={() => setSyncNonce((n) => n + 1)}
        />
        <CtaStack>
          <PrimaryYellowLink to={`/login?return=${loginReturnParam}`}>Sign in</PrimaryYellowLink>
        </CtaStack>
        <MutedSmall style={{ marginTop: "1rem" }}>
          Don&apos;t have an account?{" "}
          <Link
            to={`/register?return=${signupReturnParam}`}
            style={{ fontWeight: 700, color: "#232323" }}
          >
            Create one
          </Link>
        </MutedSmall>
      </CardBase>
    );

  const deviceInfoCard =
    !claimSuccess &&
    hasDeviceId &&
    !(authReady && !isLoggedIn && hasPairingToken) && (
      <CardBase>
        {loading && <LoadingLine>Looking up your frame…</LoadingLine>}
        {claiming && <LoadingLine>Claiming your frame…</LoadingLine>}
        {error && <ErrorText>{error}</ErrorText>}

        {syncData && !loading && (
          <>
            <Row style={{ marginTop: error ? "0.75rem" : 0 }}>
              <Badge>status: {syncData.status || "unknown"}</Badge>
              <Badge>action: {syncData.action || "unknown"}</Badge>
            </Row>

            {syncData.action === "show_registration" && (
              <ScreenRefreshInfo
                nextRefreshAt={syncData.registration?.nextRefreshAt}
                onExpire={() => setSyncNonce((n) => n + 1)}
              />
            )}

            {syncData.action === "show_sale" && (
              <div style={{ marginTop: "0.9rem" }}>
                <h3 style={{ marginBottom: "0.4rem" }}>this display is currently for sale</h3>
                <Muted style={{ marginTop: 0 }}>
                  {syncData.sale?.description || "contact us for availability and details."}
                </Muted>
                <Muted style={{ marginTop: "0.25rem", marginBottom: "0.25rem" }}>
                  price: {syncData.sale?.price ?? 4500} {syncData.sale?.currency ?? "EUR"}
                </Muted>
                <Muted style={{ marginTop: "0.25rem" }}>
                  contact: {syncData.sale?.contactEmail || "hello@n450s.com"}
                </Muted>
                {syncData.sale?.buyUrl && (
                  <a href={syncData.sale.buyUrl} style={{ fontWeight: 700 }}>
                    go to buy page
                  </a>
                )}
              </div>
            )}
          </>
        )}

        <Section>
          {!authReady ? (
            <LoadingLine>Checking your session…</LoadingLine>
          ) : !isLoggedIn ? (
            <>
              <Muted style={{ marginBottom: "0.75rem" }}>
                Sign in to claim this display
                {!hasPairingToken ? " (open the claim link from your frame's QR for a valid code)." : ""}
              </Muted>
              <Button variant="sky" asChild>
                <Link to={`/login?return=${loginReturnParam}`}>Sign in</Link>
              </Button>
            </>
          ) : (
            <Button
              variant="default"
              onClick={handleClaim}
              disabled={!canFetch || claiming || loading || syncData?.action === "show_sale"}
              loading={claiming}
            >
              claim this display
            </Button>
          )}
        </Section>
      </CardBase>
    );

  const manualEntryCard =
    !claimSuccess && !hasDeviceId && !deviceIdFromUrl && (
      <CardBase>
        <CardHeading>Can&apos;t scan? Enter the ID manually</CardHeading>
        <CardSub>
          Find the 4-character display ID on the e-paper screen below the QR code.
        </CardSub>
        <Row>
          <DeviceInput
            id="aotd-irl-device-id"
            value={deviceId}
            onChange={(e) => setDeviceId(normalizeDeviceId(e.target.value))}
            placeholder="e.g. A1B2"
            aria-label="Display ID"
          />
          <Button type="button" variant="secondary" onClick={handleLookup}>
            Look up
          </Button>
        </Row>
        {error && <ErrorText>{error}</ErrorText>}
        {loading && <LoadingLine>Looking up your frame…</LoadingLine>}
      </CardBase>
    );

  const bootstrappingCard = (
    <CardBase>
      <LoadingLine>Checking your session…</LoadingLine>
    </CardBase>
  );

  let primaryCard: React.ReactNode;
  if (claimSuccess && displayIdForCopy) {
    primaryCard = successCard;
  } else if (!authReady) {
    primaryCard = bootstrappingCard;
  } else if (signInPromptCard) {
    primaryCard = signInPromptCard;
  } else if (hasDeviceId) {
    primaryCard = deviceInfoCard;
  } else if (!deviceIdFromUrl) {
    primaryCard = manualEntryCard;
  } else {
    primaryCard = (
      <CardBase>
        <CardHeading>Display ID missing</CardHeading>
        <Muted style={{ marginBottom: "1rem" }}>
          This claim link referenced a frame, but the ID field is empty. Restore it from the link or
          re-scan the QR.
        </Muted>
        <Button type="button" variant="secondary" onClick={() => setDeviceId(initialDeviceId)}>
          Restore display ID from link
        </Button>
      </CardBase>
    );
  }

  return (
    <Wrap>
      <ContentMax>
        <PageTitle>Claim your AOTD frame</PageTitle>
        <PageSubtitle>
          Scan the QR on your frame, sign in, and link the display to your account so it can show
          your art.
        </PageSubtitle>

        {primaryCard}

        <FooterLink>
          <Link to="/aotd-irl/setup">Need help? See setup guide →</Link>
        </FooterLink>
      </ContentMax>
    </Wrap>
  );
};

export default AotdIrlClaim;
